import { Component, computed, inject, Input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule } from 'primeng/paginator';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AuctionListingHorizontalViewComponent } from '../../auction-listing/auction-listing-horizontal-view/auction-listing-horizontal-view.component';
import { AuctionListingVerticalViewComponent } from '../../auction-listing/auction-listing-vertical-view/auction-listing-vertical-view.component';
import { SelectModule } from 'primeng/select';
import { SearchService } from '../../../../services/bidder-community/search/search.service';
import { ActivatedRoute } from '@angular/router';
import { faImage, faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { isPlatformBrowser } from "@angular/common";
import { IAuctionListing } from '../../../../models/IAuctionListing';
import { CustomBreadcrumbComponent } from '../../custom-breadcrumb/custom-breadcrumb.component';
import { CustomBreadcrumbService } from '../../../../services/custom-breadcrumb/custom-breadcrumb.service';
import { AbsoluteMediaUriService } from '../../../../services/absolute-media-uri.service';
import { ICustomMenuItem } from '../../../../models/ICustomMenuItem';
import { MenuItem } from 'primeng/api';



@Component({
  selector: 'app-auctions-only',
  imports: [PaginatorModule,
    ButtonModule,
    SelectButtonModule,
    AuctionListingHorizontalViewComponent,
    AuctionListingVerticalViewComponent,
    SelectModule, FontAwesomeModule,
    FormsModule,
    CustomBreadcrumbComponent
  ],
  templateUrl: './auctions-only.component.html',
  styleUrl: './auctions-only.component.scss'
})
export class AuctionsOnlyComponent implements OnInit {

  @Input() query: string | null = null;

  faImage = faImage;
  faList = faList;

  protected icons = {
    faImage: faImage,
    faList: faList
  }

  private readonly searchService = inject(SearchService)
  private route = inject(ActivatedRoute);
  private readonly platformId = inject(PLATFORM_ID);


  viewMode: 'image' | 'list' = 'image';
  ItemListOptions: any[] = [{ label: 'Image View', value: 'image' }, { label: 'List View', value: 'list' }];

  page: number = 0;
  rowsPerPageOptions = [10, 20, 30, 40];
  first = signal(0);
  per = signal(10);

  selectedSortSignal = signal('NAME_ASC');
  auctionsTotal!: number;
  isMobile!: boolean;

  // breadcrumb
  private readonly customBreadcrumbService = inject(CustomBreadcrumbService);
  private readonly absoluteMediaUriService = inject(AbsoluteMediaUriService);

  itemsBreadcrumb: ICustomMenuItem[] | undefined;
  home!: MenuItem;
  responsiveOptions: any[] = [
    {
      breakpoint: '1300px',
      numVisible: 4
    },
    {
      breakpoint: '575px',
      numVisible: 1
    }
  ];


  auctionsResultSignal = this.searchService.auctionsResultSignal;

  sortFilters = [
    { label: 'Item Name - Ascending', value: 'NAME_ASC' },
    { label: 'Item Name - Descending', value: 'NAME_DESC' },
    { label: 'Time Left - Ascending', value: 'TIMELEFT_ASC' },
    { label: 'Time Left - Descending', value: 'TIMELEFT_DESC' },
  ];

  sortedItems = computed(() => {
    const sortValue = this.selectedSortSignal();
    return [...this.auctionsResultSignal()].sort((a, b) => {

      switch (sortValue) {
        case 'NAME_ASC':
          return a.auctionName.localeCompare(b.auctionName);
        case 'NAME_DESC':
          return b.auctionName.localeCompare(a.auctionName);

        case 'TIMELEFT_DESC':
          {
            const now = new Date();
            const aClosed = new Date(a.auctionClosingDate) < now;
            const bClosed = new Date(b.auctionClosingDate) < now;

            if (aClosed !== bClosed) {
              return aClosed ? -1 : 1;
            }

            const diff = new Date(b.auctionClosingDate).getTime() - new Date(a.auctionClosingDate).getTime();
            if (diff !== 0) return diff;

            return b.auctionName.localeCompare(a.auctionName);
          }
        case 'TIMELEFT_ASC':
        default: {
          const now = new Date();
          const aClosed = new Date(a.auctionClosingDate) < now;
          const bClosed = new Date(b.auctionClosingDate) < now;

          if (aClosed !== bClosed) {
            return aClosed ? 1 : -1;
          }

          const diff = new Date(a.auctionClosingDate).getTime() - new Date(b.auctionClosingDate).getTime();
          if (diff !== 0) return diff;

          return a.auctionName.localeCompare(b.auctionName);
        }
      }
    });
  });

  ngOnInit(): void {

    if (isPlatformBrowser(this.platformId)) {

      this.isMobile = window.innerWidth < 1024;
      if (this.isMobile) {
        this.viewMode = 'image';
      } else {
        this.viewMode = this.viewMode ?? 'list';
      }


      window.addEventListener('resize', () => {
        this.viewMode = window.innerWidth < 1200
          ? 'image'
          : (this.viewMode ?? 'image');
      });
    }

    this.route.queryParams.subscribe(params => {
      this.query = params['q'] ?? '';
      if (this.query) {
        this.getAuctions(this.query);
      }
    });

  }

  getAuctions(q: string) {

    this.searchService.getAuctionResult(q ?? '').subscribe(result => {

      this.auctionsTotal = result.length ?? 0;

      this.itemsBreadcrumb = [
        {
          label: 'BiddingForGood',
          url: this.absoluteMediaUriService.getAbsoluteMediaUri('/auction/biddingforgood.action')
        },
        { label: `Search Results (${this.auctionsTotal})` },
      ];

      this.customBreadcrumbService.setItems(this.itemsBreadcrumb);
    });
  }

  onPageChange(event: any) {
    this.first.set(event.first);
    this.per.set(event.rows);

    this.page = event.page + 1;

    this.getAuctions(this.query!);
  }

  onMyAuctionsChanged(myAuction: IAuctionListing | null): void {
    this.auctionsResultSignal.update(list => {
      if (!list) return list;
      return list.map(item =>
        item.auctionId === myAuction?.auctionId
          ? { ...item, isFavorite: !item.isFavorite }
          : item
      );
    });
  }



}
